'use client';

import React from 'react';
import ContentSection from '@/components/organisms/level3/ContentSection';
import styles from './SustainabilityContent.module.scss';

interface SustainabilityContentProps {
  description: string;
  sections: {
    title: string;
    content: React.ReactNode;
    icon?: string;
  }[];
  initiatives?: {
    title: string;
    description: string;
    status: 'active' | 'planned' | 'completed';
    icon?: string;
  }[];
  metrics?: {
    label: string;
    value: string;
    trend?: 'up' | 'down' | 'stable';
    description?: string;
  }[];
}

export function SustainabilityContent({ 
  description, 
  sections, 
  initiatives,
  metrics 
}: SustainabilityContentProps) {
  return (
    <div className={styles.sustainabilityContent}>
      {/* Overview Section */}
      <ContentSection>
        <h2 className={styles.sectionTitle}>概要</h2>
        <div className={styles.overview}>
          <p className={styles.description}>{description}</p>
        </div>
      </ContentSection>

      {/* Metrics Section */}
      {metrics && metrics.length > 0 && (
        <ContentSection>
          <h2 className={styles.sectionTitle}>主要指標</h2>
          <div className={styles.metrics}>
            {metrics.map((metric, index) => (
              <div key={index} className={styles.metric}>
                <div className={styles.metricValue}>
                  {metric.value}
                  {metric.trend && (
                    <span className={`${styles.trend} ${styles[metric.trend]}`}>
                      {metric.trend === 'up' ? '↗' : metric.trend === 'down' ? '↘' : '→'}
                    </span>
                  )}
                </div>
                <div className={styles.metricLabel}>{metric.label}</div>
                {metric.description && (
                  <div className={styles.metricDescription}>{metric.description}</div>
                )}
              </div>
            ))}
          </div>
        </ContentSection>
      )}

      {/* Main Content Sections */}
      {sections.map((section, index) => (
        <ContentSection key={index}>
          <h2 className={styles.sectionTitle}>{section.title}</h2>
          <div className={styles.sectionContent}>
            {section.icon && (
              <div className={styles.sectionIcon}>
                {section.icon}
              </div>
            )}
            <div className={styles.sectionText}>
              {section.content}
            </div>
          </div>
        </ContentSection>
      ))}

      {/* Initiatives Section */}
      {initiatives && initiatives.length > 0 && (
        <ContentSection>
          <h2 className={styles.sectionTitle}>主な取り組み</h2>
          <div className={styles.initiatives}>
            {initiatives.map((initiative, index) => (
              <div key={index} className={`${styles.initiative} ${styles[initiative.status]}`}>
                <div className={styles.initiativeHeader}>
                  {initiative.icon && (
                    <div className={styles.initiativeIcon}>
                      {initiative.icon}
                    </div>
                  )}
                  <div className={styles.initiativeTitle}>
                    {initiative.title}
                    <span className={`${styles.status} ${styles[initiative.status]}`}>
                      {initiative.status === 'active' ? '実施中' : 
                       initiative.status === 'planned' ? '計画中' : '完了'}
                    </span>
                  </div>
                </div>
                <div className={styles.initiativeDescription}>
                  {initiative.description}
                </div>
              </div>
            ))}
          </div>
        </ContentSection>
      )}
    </div>
  );
}

export default SustainabilityContent;